import { decode } from 'jsonwebtoken';
import ApiBase, { statuses } from '../lib/ApiBase';
import { apiRootUrl } from '../config.local';
import { CandidateSubCustomerIdClaimName, candidateRole, customerRole } from './constants';
import {
	getAccessToken,
	getRefreshToken,
	logOut,
	registerRevokeTokenHandler,
	saveAccessToken,
	saveTokens
} from './session';
import AuthenticationResult from '../models/be/AuthenticationResult';

// This global is used to prevent multiple concurrent ajax requests from each trying to generate a new access token
// when the access token expires.
let generateAccessTokenPromise: Promise<Response> = null;

/**
 * @description Defines the basic interface to the API
 */
export default class AppApi extends ApiBase {
	constructor(rootUrl: string = null) {
		super(rootUrl ?? apiRootUrl);
	}

	// eslint-disable-next-line class-methods-use-this
	async getAccessToken(): Promise<string> {
		const accessToken = await AppApi.getOrGenerateAccessToken();
		if (accessToken === null) {
			logOut(true);
			await new Promise(() => {});
			// Awaiting a never-resolved promise to effectively stop execution so that this fn's promise won't
			// resolve prior to navigation to login.
		}
		return accessToken;
	}

	public static async generateNewAccessToken() {
		const accessToken = getAccessToken();
		const decodedAccessToken = decode(accessToken) as AccessToken;
		const authenticationData = await this.generateCustomerAccessToken(decodedAccessToken.sub, getRefreshToken());
		if (
			authenticationData &&
			(authenticationData.errorMessage === undefined || authenticationData.errorMessage === null)
		) {
			const { refreshToken, accessToken: updatedAccessToken } = authenticationData;
			if (updatedAccessToken !== null || refreshToken !== null) {
				saveTokens(refreshToken, updatedAccessToken);
			}
		}
	}

	public static async getOrGenerateAccessToken() {
		const accessToken = getAccessToken();
		const decodedAccessToken = decode(accessToken) as AccessToken;
		if (decodedAccessToken === null) return null;
		const expiryUtcSeconds = decodedAccessToken.exp;
		const millisecondsInASecond = 1000;
		const oneMinuteInSeconds = 60;
		// If the token has expired or is about to expire
		const nowInSeconds = new Date().getTime() / millisecondsInASecond;
		if (expiryUtcSeconds - nowInSeconds < oneMinuteInSeconds) {
			// TODO Use helper functions for the various cases
			switch (decodedAccessToken.role) {
				// behaviour is different for a customer because a new refresh token is issued
				// along with the access token
				case customerRole:
					// eslint-disable-next-line no-case-declarations
					const authenticationData = await this.generateCustomerAccessToken(decodedAccessToken.sub, getRefreshToken());
					if (
						authenticationData &&
						(authenticationData.errorMessage === undefined || authenticationData.errorMessage === null)
					) {
						const { refreshToken, accessToken: updatedAccessToken } = authenticationData;
						if (updatedAccessToken !== null || refreshToken !== null) {
							saveTokens(refreshToken, updatedAccessToken);
							return updatedAccessToken;
						}
						logOut(true);
						return null;
					}
					return null;

				case candidateRole:
					// eslint-disable-next-line no-case-declarations
					const newAccessToken = await this.generateCandidateAccessToken(
						decodedAccessToken.sub,
						getRefreshToken(),
						decodedAccessToken[CandidateSubCustomerIdClaimName]
					);
					if (newAccessToken === null) {
						return null;
					}
					saveAccessToken(newAccessToken);
					return newAccessToken;

				default:
					throw new Error(`Unknown token role: ${decodedAccessToken.role}.`);
			}
		} else {
			return accessToken;
		}
	}

	public static async generateCandidateAccessToken(
		email: string,
		refreshToken: string,
		subCustomerId: number
	): Promise<string> {
		if (refreshToken === null) {
			return null;
		}
		const url = `${apiRootUrl}/api/Authentication/AccessToken`;
		const body = JSON.stringify({ email, refreshToken, subCustomerId });

		if (generateAccessTokenPromise === null) {
			generateAccessTokenPromise = fetch(url, { method: 'POST', body });
		}

		// The response must be cloned as this task can be awaited on by multiple ajax requests.
		const response = (await generateAccessTokenPromise).clone();
		generateAccessTokenPromise = null;

		if (response.status === statuses.ok) {
			return response.text();
		}
		if (response.status === statuses.unauthorized) {
			return null;
		}
		throw new Error('Unexpected status code from AccessToken endpoint.');
	}

	public static async generateCustomerAccessToken(email: string, refreshToken: string): Promise<AuthenticationResult> {
		const url = `${apiRootUrl}/api/Authentication/CustomerAccessToken`;
		const body = JSON.stringify({ email, refreshToken });

		if (generateAccessTokenPromise === null) {
			generateAccessTokenPromise = fetch(url, { method: 'POST', body });
		}

		// The response must be cloned as this task can be awaited on by multiple ajax requests.
		const response = (await generateAccessTokenPromise).clone();
		generateAccessTokenPromise = null;

		if (response.status === statuses.ok) {
			return response.json();
		}
		if (response.status === statuses.unauthorized) {
			return { errorMessage: await response.text() };
		}
		return null;
	}

	public static revokeToken(accessToken: string, refreshToken: string): void {
		fetch(`${apiRootUrl}/api/Authentication/Logout`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${accessToken}`
			},
			body: JSON.stringify({ refreshToken }),
			keepalive: true
		});
	}
}

registerRevokeTokenHandler((accessToken, refreshToken) => AppApi.revokeToken(accessToken, refreshToken));

interface AccessToken {
	sub: string;
	role: string;
	exp: number;
	[CandidateSubCustomerIdClaimName]: number;
}
